import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Autosuggest from 'react-autosuggest';
import './BreedingPermissions.css';

function BreedingPermissions({ playerId }) {
  const [dogs, setDogs] = useState([]);
  const [selectedDogId, setSelectedDogId] = useState('');
  const [recipientUsername, setRecipientUsername] = useState('');
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);
  const [grantedPermissions, setGrantedPermissions] = useState([]);
  const [receivedPermissions, setReceivedPermissions] = useState([]);
  const [coBreederRequests, setCoBreederRequests] = useState([]);
  const [message, setMessage] = useState('');
  const [showDenied, setShowDenied] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!playerId) return;

    const fetchDogs = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/my-dogs-for-litters/${playerId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!res.ok) throw new Error(`Fetch failed: ${await res.text()}`);
        setDogs(await res.json());
      } catch (error) {
        setMessage('Error fetching dogs: ' + error.message);
      }
    };

    const fetchPermissions = async () => {
      try {
        const grantedRes = await fetch(`${API_BASE_URL}/my-granted-permissions/${playerId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!grantedRes.ok) throw new Error(await grantedRes.text());
        setGrantedPermissions(await grantedRes.json());

        const receivedRes = await fetch(`${API_BASE_URL}/my-received-permissions/${playerId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!receivedRes.ok) throw new Error(await receivedRes.text());
        setReceivedPermissions(await receivedRes.json());
      } catch (error) {
        setMessage('Error fetching permissions: ' + error.message);
      }
    };

    const fetchCoBreederRequests = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/co-breeder-requests/${playerId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!res.ok) throw new Error(await res.text());
        setCoBreederRequests(await res.json());
      } catch (error) {
        setMessage('Error fetching co-breeder requests: ' + error.message);
      }
    };

    fetchDogs();
    fetchPermissions();
    fetchCoBreederRequests();
  }, [playerId]);

  const fetchUsernameSuggestions = async (value) => {
    if (!value) {
      setUsernameSuggestions([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/search-players/${encodeURIComponent(value.toLowerCase())}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) throw new Error(await res.text());
      const suggestions = await res.json();
      setUsernameSuggestions(suggestions.filter(s => s !== recipientUsername));
    } catch (error) {
      setMessage('Error fetching usernames: ' + error.message);
    }
  };

  const createBreedingPermission = async () => {
    if (!selectedDogId || !recipientUsername) {
      setMessage('Please select a dog and recipient username.');
      return;
    }
    try {
      const selectedDog = dogs.find(d => d.dogId === selectedDogId);
      const res = await fetch(`${API_BASE_URL}/create-breeding-permission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ playerId, dogId: selectedDogId, recipientUsername })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(`Permission granted for ${selectedDog.registeredName} (${selectedDogId}) to ${recipientUsername}`);
      setSelectedDogId('');
      setRecipientUsername('');
      setUsernameSuggestions([]);
      const grantedRes = await fetch(`${API_BASE_URL}/my-granted-permissions/${playerId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (grantedRes.ok) setGrantedPermissions(await grantedRes.json());
    } catch (error) {
      setMessage('Error creating permission: ' + error.message);
    }
  };

  const handleCoBreederRequest = async (requestId, action) => {
    const actionValue = action === 'approve' ? 'approved' : 'denied';
    console.log(`Sending co-breeder request: requestId=${requestId}, action=${actionValue}`);
    try {
      const res = await fetch(`${API_BASE_URL}/co-breeder-request/${requestId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ action: actionValue })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message);
      const updatedRequests = await fetch(`${API_BASE_URL}/co-breeder-requests/${playerId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (updatedRequests.ok) setCoBreederRequests(await updatedRequests.json());
    } catch (error) {
      setMessage(`Error ${actionValue}ing co-breeder request: ` + error.message);
    }
  };

  if (!playerId) return <p style={{ textAlign: 'center', color: '#888' }}>Please log in to manage breeding permissions.</p>;

  const filteredRequests = showDenied ? coBreederRequests : coBreederRequests.filter(r => r.status !== 'Denied');

  return (
    <div className="breeding-permissions-container">
      <h1 className="section-title">Manage Breeding Permissions</h1>
      {message && (
        <div className="message-box">
          {message}
        </div>
      )}

      <h2 className="section-subtitle">Write a New Permission</h2>
      <div className="form-section">
        <div className="form-group">
          <label>Select Dog</label>
          <select
            value={selectedDogId}
            onChange={(e) => setSelectedDogId(e.target.value)}
            className="select-input"
          >
            <option value="">Select a Dog</option>
            {dogs.filter(d => d.status === 'Approved' && !d.isPermission).map(dog => (
              <option key={dog.dogId} value={dog.dogId}>
                {dog.registeredName} ({dog.dogId}, {dog.gender})
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Recipient Username</label>
          <Autosuggest
            suggestions={usernameSuggestions}
            onSuggestionsFetchRequested={({ value }) => fetchUsernameSuggestions(value)}
            onSuggestionsClearRequested={() => setUsernameSuggestions([])}
            getSuggestionValue={suggestion => suggestion}
            renderSuggestion={suggestion => <div className="suggestion-item">{suggestion}</div>}
            inputProps={{
              value: recipientUsername,
              onChange: (event, { newValue }) => setRecipientUsername(newValue),
              className: "text-input",
              placeholder: "Enter recipient username"
            }}
            theme={{
              container: 'autosuggest-container',
              suggestionsContainer: 'suggestions-container',
              suggestionsList: 'suggestions-list',
              suggestion: 'suggestion-item',
              suggestionHighlighted: 'suggestion-item--highlighted'
            }}
          />
        </div>
        <div className="button-group">
          <button
            className="action-button"
            onClick={createBreedingPermission}
            disabled={!selectedDogId || !recipientUsername}
          >
            Create Permission
          </button>
          <button
            className="action-button back-button"
            onClick={() => navigate('/my-litters')}
          >
            Back to Litters
          </button>
        </div>
      </div>

      <h2 className="section-subtitle">Co-Breeder Requests</h2>
      <div className="permissions-section">
        <h3 className="section-subsubtitle">Received Co-Breeder Requests</h3>
        <label className="toggle-label">
          <input
            type="checkbox"
            checked={showDenied}
            onChange={() => setShowDenied(!showDenied)}
          />
          Show Denied Requests
        </label>
        {filteredRequests.length === 0 ? (
          <p className="no-items">No co-breeder requests received.</p>
        ) : (
          <div className="requests-table-container">
            <table className="requests-table">
              <thead>
                <tr>
                  <th>Requester</th>
                  <th>Litter</th>
                  <th>Kennel</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map(request => (
                  <tr key={request._id}>
                    <td>{request.requesterPlayerId?.username || 'Unknown'}</td>
                    <td>
                      <Link to={`/litter/${request.litterId?._id}`} className="dog-link">
                        {request.litterId?.sireId} x {request.litterId?.damId}
                      </Link>
                    </td>
                    <td>{request.coBreederKennelId?.kennelName || 'Unknown'}</td>
                    <td>
                      <span
                        className={`status-badge status-${request.status.toLowerCase()}`}
                      >
                        {request.status}
                      </span>
                    </td>
                    <td>
                      {request.status === 'Pending' && (
                        <div className="button-group">
                          <button
                            className="action-button approve-button"
                            onClick={() => handleCoBreederRequest(request._id, 'approve')}
                          >
                            Approve
                          </button>
                          <button
                            className="action-button deny-button"
                            onClick={() => handleCoBreederRequest(request._id, 'deny')}
                          >
                            Deny
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <h2 className="section-subtitle">Permissions Log</h2>
      <div className="permissions-section">
        <h3 className="section-subsubtitle">Granted Permissions</h3>
        {grantedPermissions.length === 0 ? (
          <p className="no-items">No permissions granted.</p>
        ) : (
          <ul className="permissions-list">
            {grantedPermissions.map(perm => (
              <li key={perm._id} className="permission-item">
                <Link to={`/dog/${perm.dogId}`} className="dog-link">
                  {perm.dogName} ({perm.dogId})
                </Link>{' '}
                to {perm.recipientUsername} - {perm.used ? 'Used' : 'Active'}
              </li>
            ))}
          </ul>
        )}
        <h3 className="section-subsubtitle">Received Permissions</h3>
        {receivedPermissions.length === 0 ? (
          <p className="no-items">No permissions received.</p>
        ) : (
          <ul className="permissions-list">
            {receivedPermissions.map(perm => (
              <li key={perm._id} className="permission-item">
                <Link to={`/dog/${perm.dogId}`} className="dog-link">
                  {perm.dogName} ({perm.dogId})
                </Link>{' '}
                from {perm.ownerUsername} - {perm.used ? 'Used' : 'Active'}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default BreedingPermissions;